import { ref } from 'vue'
import { api } from '@/global/services/api'
import { isArray } from '@/global/utils/utils'

export function useMap (options) {
  const {
    showLoaderFn
  } = options || {}

  let leafletMap = null
  let fetchedMarkers = []
  // Total marker count across every product - used by useTable.js to decide
  // whether checkboxes are even worth showing, independent of whatever
  // subset ends up checked/rendered at any given moment.
  const totalMarkersCount = ref(0)

  const onMapLoad = (event) => {
    const { map } = event || {}
    if (!map) {
      return
    }

    leafletMap = map
    fetchMarkers()
  }

  // Fetches (and caches) every product's marker data, WITHOUT rendering
  // anything on the map - the map starts empty, markers only appear once a
  // row's checkbox is checked (see `showMarkers`). The one exception is a
  // single-marker dataset: with only one marker there's nothing meaningful
  // to pick between, so useTable.js hides the checkboxes and this shows that
  // lone marker automatically instead of leaving the map empty forever.
  const fetchMarkers = async () => {
    try {
      showLoaderFn(true)
      const { data } = await api('road-maintenance').get('products/icons')

      if (!isArray(data)) {
        return
      }

      fetchedMarkers = data
      const allIcons = data
        .flatMap(item => item.icons)
        .filter(icon => icon?.id && icon?.lat && icon?.lon) // ensure that every marker has id, lat and lon props
      totalMarkersCount.value = allIcons.length

      if (allIcons.length === 1) {
        renderMarkers(allIcons)
      }
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      showLoaderFn(false)
    }
  }

  const renderMarkers = (icons) => {
    if (!icons.length) {
      // `generateMarkers` only clears the existing marker group when it's
      // given a non-empty list (see MapBase.vue) - an empty selection has to
      // clear it explicitly instead.
      leafletMap?.removeMarkers()
      return
    }

    leafletMap?.onMapReady(function (map) {
      map.generateMarkers({
        markers: icons,
        markerClusterOptions: {
          maxClusterRadius: 40
        },
        fitMarkers: true,
        fitBoundsOptions: {
          pad: 0.5
        }
      })
    })
  }

  const showMarkers = (event) => {
    const { data } = event || {}
    if (!leafletMap) {
      return
    }

    // collect ids (product_ids) from event data
    const productIds = (isArray(data) ? data : []).map(obj => obj?.id).filter(Boolean)

    // find ALL markers belonging to these product_ids
    const selectedIcons = fetchedMarkers
      .filter(marker => productIds.includes(marker?.product_id))
      .flatMap(marker => marker?.icons || [])
      .filter(icon => icon?.id && icon?.lat && icon?.lon)

    renderMarkers(selectedIcons)
  }

  return {
    onMapLoad,
    showMarkers,
    fetchMarkers,
    totalMarkersCount
  }
}
